import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { SmbOutreachAutomationBanner } from '../../components/smb/SmbOutreachAutomationBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Users, TrendingUp, DollarSign, AlertTriangle, Clock, BarChart3, Target } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: '#3b82f6', contacted: '#06b6d4', call_declined: '#f97316', preview_sent: '#8b5cf6',
  preview_qa_passed: '#10b981', followup: '#eab308', converted: '#22c55e', lost: '#ef4444',
};

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', call_declined: 'Not Interested', preview_sent: 'Preview Sent',
  preview_qa_passed: 'QA Passed', followup: 'Follow Up', converted: 'Converted', lost: 'Lost',
};

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

export const SMBDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/smb/dashboard');
        if (res.data.success) setData(res.data.data);
      } catch { toast.error('Failed to load dashboard'); }
    };
    fetch();
  }, []);

  if (!data) return <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground">Loading...</div>;

  const conversionRate = data.total_leads > 0 ? ((data.total_conversions / data.total_leads) * 100).toFixed(1) : 0;
  const statusOrder = ['new', 'contacted', 'call_declined', 'preview_sent', 'preview_qa_passed', 'followup', 'converted', 'lost'];
  const maxStatusCount = Math.max(...Object.values(data.status_counts || {}), 1);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-dashboard">
      <SmbOutreachAutomationBanner />
      <div>
        <h1 className="text-2xl font-bold">SMB Performance</h1>
        <p className="text-muted-foreground text-sm">Sales pipeline analytics and team performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card data-testid="kpi-total-leads"><CardContent className="p-4 text-center">
          <Users className="h-5 w-5 mx-auto mb-1 text-blue-400" />
          <p className="text-2xl font-bold">{data.total_leads}</p><p className="text-xs text-muted-foreground">Total Leads</p>
        </CardContent></Card>
        <Card data-testid="kpi-conversions"><CardContent className="p-4 text-center">
          <Target className="h-5 w-5 mx-auto mb-1 text-green-400" />
          <p className="text-2xl font-bold">{data.total_conversions}</p><p className="text-xs text-muted-foreground">Conversions</p>
        </CardContent></Card>
        <Card data-testid="kpi-revenue"><CardContent className="p-4 text-center">
          <DollarSign className="h-5 w-5 mx-auto mb-1 text-emerald-400" />
          <p className="text-2xl font-bold">{formatCurrency(data.total_revenue)}</p><p className="text-xs text-muted-foreground">Revenue</p>
        </CardContent></Card>
        <Card data-testid="kpi-conversion-rate"><CardContent className="p-4 text-center">
          <TrendingUp className="h-5 w-5 mx-auto mb-1 text-cyan-400" />
          <p className="text-2xl font-bold">{conversionRate}%</p><p className="text-xs text-muted-foreground">Conversion Rate</p>
        </CardContent></Card>
        <Card data-testid="kpi-overdue"><CardContent className="p-4 text-center">
          <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-400" />
          <p className="text-2xl font-bold text-red-400">{data.overdue_followups}</p><p className="text-xs text-muted-foreground">Overdue</p>
        </CardContent></Card>
        <Card data-testid="kpi-today"><CardContent className="p-4 text-center">
          <Clock className="h-5 w-5 mx-auto mb-1 text-yellow-400" />
          <p className="text-2xl font-bold">{data.today_followups}</p><p className="text-xs text-muted-foreground">Today's Tasks</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Pipeline Funnel */}
        <Card data-testid="pipeline-funnel">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" />Pipeline Breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {statusOrder.map(status => {
              const count = data.status_counts?.[status] || 0;
              const pct = maxStatusCount > 0 ? (count / maxStatusCount) * 100 : 0;
              return (
                <div key={status} className="flex items-center gap-3" data-testid={`funnel-${status}`}>
                  <span className="text-xs w-20 text-right text-muted-foreground">{STATUS_LABELS[status]}</span>
                  <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(pct, count > 0 ? 8 : 0)}%`, backgroundColor: STATUS_COLORS[status] }}>
                      {count > 0 && <span className="text-[10px] font-bold text-white">{count}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Category & City Breakdown */}
        <div className="space-y-5">
          <Card data-testid="category-breakdown">
            <CardHeader className="pb-3"><CardTitle className="text-base">By Category</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {(data.categories || []).map(c => (
                  <Badge key={c.name} variant="outline" className="capitalize px-3 py-1">{c.name} <span className="ml-1 font-bold">{c.count}</span></Badge>
                ))}
                {(!data.categories || data.categories.length === 0) && <p className="text-sm text-muted-foreground">No data</p>}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="city-breakdown">
            <CardHeader className="pb-3"><CardTitle className="text-base">By City</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {(data.cities || []).map(c => (
                  <Badge key={c.name} variant="outline" className="px-3 py-1">{c.name} <span className="ml-1 font-bold">{c.count}</span></Badge>
                ))}
                {(!data.cities || data.cities.length === 0) && <p className="text-sm text-muted-foreground">No data</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Performance Table */}
      <Card data-testid="team-performance">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Team Performance</CardTitle></CardHeader>
        <CardContent>
          {(data.performance || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No performance data yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team Member</TableHead>
                  <TableHead className="text-center">Leads</TableHead>
                  <TableHead className="text-center">Activities</TableHead>
                  <TableHead className="text-center">Conversions</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.performance.map(p => (
                  <TableRow key={p.user_id} data-testid={`perf-row-${p.user_id}`}>
                    <TableCell className="font-medium">{p.user_name}</TableCell>
                    <TableCell className="text-center">{p.leads_assigned}</TableCell>
                    <TableCell className="text-center">{p.activities}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={p.conversions > 0 ? 'bg-green-500/15 text-green-400' : 'bg-muted text-muted-foreground'}>{p.conversions}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(p.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
